import { useCallback, useEffect, useRef } from "react"
import { useRouter } from "next/router"

import type { QueryClient, QueryKey } from "@tanstack/react-query"

import { getRouteOrganizationSlug } from "@/utils/workspaceRoutes"

import { subscribeToDashboardDeleted } from "../utils/dashboardDeletedChannel"
import { createDashboardDeletedSaveError } from "./useDashboardWidgetSaveQueue"

interface DashboardDeletedElsewhereParams {
  cancelDashboardSaves: (dashboardId: string, error: Error) => void
  clearWidgetOverride: (dashboardId: string) => void
  currentDashboardId: string | undefined
  goHome: (organizationSlug: string) => void
  invalidateDashboardQueries: (dashboardId?: string) => void
  layoutQueryKey: (dashboardId: string) => QueryKey
  onCurrentDashboardDeleted: () => void
  organizationSlug: string | undefined
  queryClient: QueryClient
}

// Applies another tab's dashboard deletion to this one. Read through a ref so
// the channel subscribes once rather than on every render. Navigation waits for
// the organization to load, so the redirect never uses an unverified slug.
export function useDashboardDeletedElsewhere(
  params: DashboardDeletedElsewhereParams,
) {
  const router = useRouter()
  const paramsRef = useRef(params)
  const homeRedirectPendingRef = useRef(false)
  const routeSlugRef = useRef<string | null>(null)
  useEffect(() => {
    paramsRef.current = params
    routeSlugRef.current = router.asPath
      ? getRouteOrganizationSlug(router.asPath)
      : null
  })
  // Redirects once the organization Clerk has loaded is the one the route names.
  const flushHomeRedirect = useCallback(() => {
    const { goHome, organizationSlug } = paramsRef.current
    if (
      homeRedirectPendingRef.current &&
      organizationSlug &&
      organizationSlug === routeSlugRef.current
    ) {
      homeRedirectPendingRef.current = false
      goHome(organizationSlug)
    }
  }, [])
  // A pending redirect belongs to the page it was set on, not to whatever the
  // user navigates to before the organization loads.
  useEffect(() => {
    homeRedirectPendingRef.current = false
  }, [router.asPath])
  useEffect(() => {
    flushHomeRedirect()
  }, [flushHomeRedirect, params])
  useEffect(
    () =>
      subscribeToDashboardDeleted(({ dashboardId, organizationSlug }) => {
        if (organizationSlug !== routeSlugRef.current) {
          return
        }
        const latest = paramsRef.current
        latest.cancelDashboardSaves(
          dashboardId,
          createDashboardDeletedSaveError(),
        )
        latest.clearWidgetOverride(dashboardId)
        latest.queryClient.removeQueries({
          queryKey: latest.layoutQueryKey(dashboardId),
        })
        latest.invalidateDashboardQueries()
        if (dashboardId === latest.currentDashboardId) {
          latest.onCurrentDashboardDeleted()
          homeRedirectPendingRef.current = true
          flushHomeRedirect()
        }
      }),
    [flushHomeRedirect],
  )
}
